import type { RotateTuning, ScalePulseTuning, SymbolWinBrightnessTuning, WinAmountLabelFlareTuning, WinHighlightTuning } from "@slot/presentation";

/**
 * Game tuning VALUES for the win presentation: the win HIGHLIGHT (the dim, the winners' animation, the flare amount
 * label) on plain reels with paylines. A line's win stays on the grid until the next spin. Shapes are
 * `@slot/presentation`'s (`WinHighlightTuning`, `WinAmountLabelFlareTuning`, `ScalePulseTuning`, `RotateTuning`,
 * `SymbolWinBrightnessTuning`); the values are the Cocos title's (`assets/scripts/game/tuning/winPresentationTuning.ts`).
 *
 * Consumers (all wired in createPresentation): `WinHighlightEffect` (`.layer`, `.label` through its label tuning), the
 * winners' sprite composite (`.scalePulse`, `.scalePulseL`, `.rotate`, `.brightness`, `.symbolFrames`), the shell's win
 * sequence (`.winHighlightDelay`, its idle `.cycle`) and its auto-advance hold (`.autoAdvance`).
 */

/**
 * Symbols that get the extra win ROTATION on top of the pulse: the L (low-pay) tier. Explicit, so the wild (W), the
 * VS and the scatter (FS) never wobble on a winning line. The H symbols switch frames instead (symbolWinFrames.ts).
 */
export const SYMBOL_WIN_ROTATE_SYMBOL_IDS: readonly string[] = ["L1", "L2", "L3", "L4", "L5"];

export interface WinPresentationTuning {
  layer: WinHighlightTuning;
  label: WinAmountLabelFlareTuning;
  scalePulse: ScalePulseTuning;
  scalePulseL: ScalePulseTuning;
  rotate: RotateTuning;
  brightness: SymbolWinBrightnessTuning;
  symbolFrames: { fadeInSeconds: number; startDelayMilliseconds: number; baseFadeOutDelaySeconds: number };
  autoAdvance: { postWinHoldSeconds: number; postRoundHoldSeconds: number };
  winHighlightDelay: { firstSeconds: number; subsequentSeconds: number };
  cycle: { interCyclePauseSeconds: number };
}

export const WIN_PRESENTATION_TUNING: WinPresentationTuning = {
  layer: {
    cycleDurationMilliseconds: 400,

    // Lead-in before the dim starts (0 = immediately at highlight time).
    dimStartDelayMilliseconds: 0,
    dimFadeSeconds: 0.05,
    // Darkens the symbol COLOUR (opaque) via negative exposure; more negative = darker (-0.5 ≈ ×0.5).
    dimExposure: -0.6,
    dimFadeEasing: "quadOut",
    // The winners dim too, and each lights up as its shine starts (the exposure shine owns the dim).
    dimWinners: true,

    // Faded un-dim for the win-cycle breathing (the dim lingers, then fades back between cycles). The spin-start clear
    // snaps the dim regardless, so a fast slam re-spin never carries still-dimmed cells onto the new winners.
    dimEndStartDelayMilliseconds: 500,
    dimEndFadeSeconds: 0.2,

    labelInStartDelayMilliseconds: 100,
    labelOutStartDelayMilliseconds: 1100,
    // No merged labels in this title (no deferred multiplier merge, no substep total): inert.
    labelMergeLingerMilliseconds: 500,

    // Per-symbol sweep: each winning cell's animation starts this many ms after the previous one (left → right), in
    // step with the payline draw.
    symbolAnimStaggerMilliseconds: 20,
  },

  label: {
    // Appear BIG, then settle to normal scale (1.0).
    appearScale: 1.4,
    appearScaleSeconds: 0.3,
    appearScaleEasing: "quadOut",
    // Opacity fade-in on appear; then HOLDS at full until the layer hides it (hideSeconds).
    fadeInSeconds: 0.12,
    // Brightness FLARE on appear (an ExposureFilter on the digits), ramped peak → 0 (bright → normal). 0 = no flare.
    shinePeak: 1.5,
    shineSeconds: 0.35,
    shineEasing: "quadOut",
    hideSeconds: 0.3,
    // A multiplied line's "base x mult → final" label in the win cycle: "base x mult" for holdSeconds, then a popScale
    // bump swaps the text to the final amount at its peak.
    multiplier: {
      holdSeconds: 0.7,
      popSeconds: 0.3,
      popScale: 1.3,
    },
  },

  // The winners' scale-pulse: a Y-only squash-and-stretch (squeeze → pop → settle); X held at base.
  scalePulse: {
    scaleInStartDelayMilliseconds: 80,
    squeezeFactor: 1, // 1 skips the squeeze
    squeezeSeconds: 0.08,
    squeezeEasing: "quadOut",
    scaleFactor: 1.2,
    scaleUpSeconds: 0.1,
    scaleUpEasing: "quadOut",
    scaleHoldSeconds: 0,
    scaleDownSeconds: 0.1,
    scaleDownEasing: "quadIn",
  },

  // The L symbols' pulse: on BOTH axes, with a short hold at the peak.
  scalePulseL: {
    uniformScale: true,
    scaleInStartDelayMilliseconds: 80,
    squeezeFactor: 1,
    squeezeSeconds: 0.08,
    squeezeEasing: "quadOut",
    scaleFactor: 1.2,
    scaleUpSeconds: 0.08,
    scaleUpEasing: "quadOut",
    scaleHoldSeconds: 0.05,
    scaleDownSeconds: 0.08,
    scaleDownEasing: "quadIn",
  },

  // The L symbols' wobble, once per win, timed with the pulse (+ = CCW).
  rotate: {
    degrees: 5,
    startDelayMilliseconds: 80,
    rotateUpSeconds: 0.1,
    rotateUpEasing: "quadOut",
    holdSeconds: 0,
    rotateDownSeconds: 0.1,
    rotateDownEasing: "quadIn",
  },

  // The winners' brightness shine (exposure lift, rgb *= 1 + highlight), once per win: `SymbolWinBrightnessEffect`,
  // which also owns the dim.
  brightness: {
    peak: 2,
    startDelayMilliseconds: 80,
    rampUpSeconds: 0.05,
    rampUpEasing: "quadOut",
    holdSeconds: 0.0,
    rampDownSeconds: 0.02,
    rampDownEasing: "quadIn",
  },

  // The H symbols cross-fade to their alt frame (H1-2, …) and HOLD it through the win cycles, until the next spin.
  symbolFrames: {
    fadeInSeconds: 0.05,
    startDelayMilliseconds: 100,
    // The outgoing base frame holds this long before fading out, so the incoming overlay covers the cell first.
    baseFadeOutDelaySeconds: 0.015,
  },

  autoAdvance: {
    // The hold after a round before the next AUTO-ADVANCED spin (free spins, autospin): a win lingers, a no-win round
    // gets a short breath (so super turbo stays readable). Constant perceived length across speed modes.
    postWinHoldSeconds: 0.5,
    postRoundHoldSeconds: 0.25,
  },

  // The beat before each win in the first pass: the spin's first win after the reels land, then each next one.
  winHighlightDelay: {
    firstSeconds: 0.1,
    subsequentSeconds: 0.0,
  },

  // The idle cycle after the first pass: the grid bright for this long, then the spin's lines re-shown one by one (each
  // on the highlight's beat, `layer.cycleDurationMilliseconds`), the grid bright again, until the next spin.
  cycle: {
    interCyclePauseSeconds: 2,
  },
};
